import type { CommentObjectLike, CommentTarget, PlanRoom } from "@sts/geometry";
import type { PlanObject } from "../api/types.js";
import { KIND_NAMES } from "./plan-objects.js";

/**
 * The portal's side of comments pinned to the plan: plan objects as the pin
 * geometry sees them, and what a pin is on, in words. The geometry itself —
 * what a click is on, following an element — is `@sts/geometry`'s
 * `plan-comments.ts`, shared with the installer pack.
 */

/** A fitting is a symbol, not a size: this much around it counts as on it. */
const FITTING_FOOTPRINT_M = 0.08;

/** Plan objects with their footprints, so a click on the end of a radiator is on the radiator. */
export function commentObjects(objects: readonly PlanObject[]): CommentObjectLike[] {
  return objects.map((object) => {
    switch (object.kind) {
      case "radiator":
        return {
          id: object.id,
          storey: object.storey,
          geometry: object.geometry,
          footprintM: { w: object.spec.lengthMm / 1000, d: object.spec.depthMm / 1000 },
        };
      case "heat-pump":
        return {
          id: object.id,
          storey: object.storey,
          geometry: object.geometry,
          footprintM: { w: object.spec.widthMm / 1000, d: object.spec.depthMm / 1000 },
        };
      case "fitting":
        return {
          id: object.id,
          storey: object.storey,
          geometry: object.geometry,
          footprintM: { w: FITTING_FOOTPRINT_M, d: FITTING_FOOTPRINT_M },
        };
      case "cylinder":
      case "buffer":
        // Round: its diameter both ways.
        return {
          id: object.id,
          storey: object.storey,
          geometry: object.geometry,
          footprintM: { w: object.spec.diameterMm / 1000, d: object.spec.diameterMm / 1000 },
        };
      case "pipe":
        return { id: object.id, storey: object.storey, geometry: object.geometry };
    }
  });
}

/**
 * What a pin is on: "Radiator · Under window", "Wall 3 · Lounge", "Lounge".
 * Null for a pin on open floor. An element no longer on the plan says so.
 */
export function commentTargetLabel(
  target: CommentTarget | null | undefined,
  rooms: readonly PlanRoom[],
  objects: readonly PlanObject[],
): string | null {
  if (!target) return null;
  switch (target.kind) {
    case "object": {
      const object = objects.find((o) => o.id === target.id);
      if (!object) return "An object no longer on the plan";
      return object.label ? `${KIND_NAMES[object.kind]} · ${object.label}` : KIND_NAMES[object.kind];
    }
    case "room":
      return rooms.find((room) => room.floorIds.includes(target.id))?.label ?? "A room no longer on the plan";
    case "wall":
    case "opening": {
      for (const room of rooms) {
        const segment = room.segments.find((s) => s.id === target.id);
        if (segment) return `${segment.label} · ${room.label}`;
      }
      return target.kind === "wall" ? "A wall no longer on the plan" : "A window or door no longer on the plan";
    }
  }
}

/** "11 Sep, 14:05" — when a comment was written, in the reader's own time. */
export function commentWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
